import React from "react";
import {
  AbsoluteFill,
  useCurrentFrame,
  interpolate,
  spring,
  Audio,
  staticFile,
  Sequence,
} from "remotion";
import {CLAMP, FONT_STACK, IDV} from "../presets/idv";
import {AccentLine} from "../components/dimus/AccentLine";
import {KineticText} from "../components/text/KineticText";
import {Noise} from "../components/dimus/Noise";
import {Vignette} from "../components/dimus/Vignette";
import {FilmGrain} from "../components/dimus/FilmGrain";

/**
 * CAFÉ COM TEU ROI EP20 — "C, A, C Blended Sangrando"
 *
 * Estrutura 5 cenas:
 *  S1 HOOK         — R$10k / 2 clientes / R$5k
 *  S2 DEFINIÇÃO    — C,A,C blended explicado
 *  S3 CONTA QUE FALTA — LTV introduzido (caso saudável)
 *  S4 PAYBACK      — caso ruim (sangrando)
 *  S5 MISSÃO DO DIA — CTA + comentário
 *
 * Editing Style: DYNAMIC
 * BGM: cafe-lofi.mp3 @ 0.15
 * TTS: ElevenLabs voice 60FHDyXNkQf05pwCUyfS (Guilherme PT-BR)
 */

const SCENES = [
  {dur: 247, file: "audio/cafe-ep20/c01.mp3", bg: "hook" as const},
  {dur: 279, file: "audio/cafe-ep20/c02.mp3", bg: "definicao" as const},
  {dur: 409, file: "audio/cafe-ep20/c03.mp3", bg: "saudavel" as const},
  {dur: 382, file: "audio/cafe-ep20/c04.mp3", bg: "sangrando" as const},
  {dur: 275, file: "audio/cafe-ep20/c05.mp3", bg: "missao" as const},
];

const TRANSITION_DUR = 25;
export const CAFE_EP20_TOTAL =
  SCENES.reduce((s, sc) => s + sc.dur, 0) + (SCENES.length - 1) * TRANSITION_DUR;
// 1592 + 100 = 1692 frames ≈ 56.4s @ 30fps

const YELLOW = IDV.third;       // #FECB18
const GREEN = IDV.secondary;    // #00DF82
const BLUE = IDV.primary;       // #1E3A61
const RED = IDV.error;          // #F87171
const WHITE = "#f5f5f7";
const MUTED = "#a3a3a7";

const BGM_VOLUME = 0.15;
const TTS_VOLUME = 0.9;

// ─── SceneFade ─────────────────────────────────
const SceneFade: React.FC<{duration: number; children: React.ReactNode}> = ({duration, children}) => {
  const frame = useCurrentFrame();
  const fadeIn = interpolate(frame, [0, 12], [0, 1], CLAMP);
  const fadeOut = interpolate(frame, [duration - 15, duration], [1, 0], CLAMP);
  const opacity = Math.min(fadeIn, fadeOut);
  const floatY = interpolate(frame, [0, 60], [0, 4], CLAMP);
  return (
    <AbsoluteFill style={{opacity, transform: `translateY(${floatY}px)`}}>
      {children}
    </AbsoluteFill>
  );
};

// ─── S1 HOOK ─────────────────────────────────
const S1 = () => {
  const f = useCurrentFrame();
  const titleSp = spring({frame: f - 8, fps: 30, config: {damping: 18, stiffness: 110}});
  return (
    <AbsoluteFill style={{background: `radial-gradient(ellipse at 50% 35%, ${BLUE}26, #000 70%)`}}>
      <Audio src={staticFile("audio/cafe-ep20/c01.mp3")} volume={TTS_VOLUME} />
      <div style={{position: "absolute", top: 280, left: 80, right: 80, transform: `scale(${0.92 + 0.08*titleSp})`, opacity: titleSp}}>
        <div style={{fontFamily: FONT_STACK, color: MUTED, fontSize: 36, fontWeight: 500, letterSpacing: 2, marginBottom: 24}}>
          ☕ CAFÉ COM TEU ROI — EP20
        </div>
        <AccentLine color={YELLOW} delay={20} />
        <div style={{fontFamily: FONT_STACK, color: WHITE, fontSize: 88, fontWeight: 800, lineHeight: 1.05, marginTop: 40}}>
          Gastou <span style={{color: YELLOW}}>R$ 10 mil</span> em marketing<br/>
          e fechou <span style={{color: YELLOW}}>2 clientes?</span>
        </div>
      </div>
      <div style={{position: "absolute", top: 1000, left: 80, right: 80}}>
        <KineticText
          text="Seu C, A, C blended é R$ 5 mil."
          startFrame={70}
          color={WHITE}
          fontSize={82}
          fontFamily={FONT_STACK}
        />
        <div style={{
          marginTop: 60,
          fontFamily: FONT_STACK, color: GREEN, fontSize: 56, fontWeight: 700,
          opacity: interpolate(f, [140, 175], [0, 1], CLAMP),
        }}>
          Bom ou ruim depende de <em>UMA conta</em>.
        </div>
      </div>
    </AbsoluteFill>
  );
};

// ─── S2 DEFINIÇÃO ─────────────────────────────────
const S2 = () => {
  const f = useCurrentFrame();
  return (
    <AbsoluteFill style={{background: "#0a0a0a"}}>
      <Audio src={staticFile("audio/cafe-ep20/c02.mp3")} volume={TTS_VOLUME} />
      <div style={{position: "absolute", top: 220, left: 80, right: 80}}>
        <div style={{fontFamily: FONT_STACK, color: MUTED, fontSize: 36, letterSpacing: 2, marginBottom: 16}}>DEFINIÇÃO</div>
        <AccentLine color={YELLOW} delay={6} />
        <div style={{
          fontFamily: FONT_STACK, color: WHITE, fontSize: 76, fontWeight: 800, marginTop: 60, lineHeight: 1.1,
          opacity: interpolate(f, [18, 45], [0, 1], CLAMP),
        }}>
          C, A, C blended =
        </div>
        <div style={{
          marginTop: 40, padding: 40, borderRadius: 24, background: `${BLUE}40`, border: `2px solid ${YELLOW}`,
          opacity: interpolate(f, [50, 85], [0, 1], CLAMP),
        }}>
          <div style={{fontFamily: FONT_STACK, color: WHITE, fontSize: 56, fontWeight: 700, lineHeight: 1.3}}>
            TUDO em <span style={{color: YELLOW}}>marketing + vendas</span><br/>
            <span style={{fontSize: 44, color: MUTED}}>÷</span><br/>
            <span style={{color: GREEN}}>novos clientes</span>
          </div>
        </div>
        <div style={{
          marginTop: 50,
          fontFamily: FONT_STACK, color: MUTED, fontSize: 42, lineHeight: 1.3,
          opacity: interpolate(f, [110, 145], [0, 1], CLAMP),
        }}>
          Inclui <strong style={{color: WHITE}}>agência</strong>, mídia, salário do time, ferramenta.<br/>
          <strong style={{color: RED}}>Não é só Meta.</strong>
        </div>
      </div>
    </AbsoluteFill>
  );
};

// ─── S3 CONTA SAUDÁVEL ─────────────────────────────────
const S3 = () => {
  const f = useCurrentFrame();
  return (
    <AbsoluteFill style={{background: `radial-gradient(ellipse at 50% 50%, ${GREEN}18, #000 75%)`}}>
      <Audio src={staticFile("audio/cafe-ep20/c03.mp3")} volume={TTS_VOLUME} />
      <div style={{position: "absolute", top: 220, left: 80, right: 80}}>
        <div style={{fontFamily: FONT_STACK, color: GREEN, fontSize: 40, letterSpacing: 2, marginBottom: 16}}>A CONTA QUE FALTA</div>
        <AccentLine color={GREEN} delay={6} />
        <div style={{
          fontFamily: FONT_STACK, color: WHITE, fontSize: 96, fontWeight: 900, marginTop: 50,
          opacity: interpolate(f, [20, 60], [0, 1], CLAMP),
        }}>
          L, T, V
        </div>
        <div style={{
          fontFamily: FONT_STACK, color: MUTED, fontSize: 44, marginTop: 8, marginBottom: 50,
          opacity: interpolate(f, [40, 80], [0, 1], CLAMP),
        }}>
          Lifetime Value
        </div>
        <div style={{
          padding: 36, borderRadius: 20, background: `${GREEN}1A`, border: `2px solid ${GREEN}`,
          opacity: interpolate(f, [90, 140], [0, 1], CLAMP),
        }}>
          <div style={{fontFamily: FONT_STACK, color: WHITE, fontSize: 46, fontWeight: 600, lineHeight: 1.35}}>
            Cliente fica <span style={{color: YELLOW}}>2 anos</span> pagando <span style={{color: YELLOW}}>R$ 1k/mês</span>
            <br/>
            <span style={{fontSize: 38, color: MUTED}}>LTV =</span> <span style={{color: GREEN, fontSize: 72, fontWeight: 900}}>R$ 24 mil</span>
          </div>
        </div>
        <div style={{
          marginTop: 50,
          fontFamily: FONT_STACK, color: WHITE, fontSize: 64, fontWeight: 800, textAlign: "center",
          opacity: interpolate(f, [220, 280], [0, 1], CLAMP),
        }}>
          R$ 24k ÷ R$ 5k = <span style={{color: GREEN, fontSize: 96}}>4,8×</span>
        </div>
        <div style={{
          marginTop: 20,
          fontFamily: FONT_STACK, color: GREEN, fontSize: 52, fontWeight: 700, textAlign: "center",
          opacity: interpolate(f, [310, 360], [0, 1], CLAMP),
        }}>
          ✓ SAUDÁVEL
        </div>
      </div>
    </AbsoluteFill>
  );
};

// ─── S4 PAYBACK SANGRANDO ─────────────────────────────────
const S4 = () => {
  const f = useCurrentFrame();
  return (
    <AbsoluteFill style={{background: `radial-gradient(ellipse at 50% 50%, ${RED}26, #0a0000 75%)`}}>
      <Audio src={staticFile("audio/cafe-ep20/c04.mp3")} volume={TTS_VOLUME} />
      <div style={{position: "absolute", top: 200, left: 80, right: 80}}>
        <div style={{fontFamily: FONT_STACK, color: RED, fontSize: 40, letterSpacing: 2, marginBottom: 16}}>MAS SE...</div>
        <AccentLine color={RED} delay={6} />
        <div style={{
          padding: 36, borderRadius: 20, background: `${RED}14`, border: `2px solid ${RED}`, marginTop: 40,
          opacity: interpolate(f, [30, 70], [0, 1], CLAMP),
        }}>
          <div style={{fontFamily: FONT_STACK, color: WHITE, fontSize: 46, fontWeight: 600, lineHeight: 1.35}}>
            Cliente paga só <span style={{color: RED}}>R$ 200/mês</span>
            <br/>
            <span style={{fontSize: 38, color: MUTED}}>LTV 2 anos =</span> <span style={{color: RED, fontSize: 72, fontWeight: 900}}>R$ 4.800</span>
          </div>
        </div>
        <div style={{
          marginTop: 50,
          fontFamily: FONT_STACK, color: WHITE, fontSize: 64, fontWeight: 800, textAlign: "center",
          opacity: interpolate(f, [120, 170], [0, 1], CLAMP),
        }}>
          R$ 4.800 ÷ R$ 5k = <span style={{color: RED, fontSize: 96}}>0,96×</span>
        </div>
        <div style={{
          marginTop: 50, padding: 32, borderRadius: 16, background: "#000",
          opacity: interpolate(f, [210, 270], [0, 1], CLAMP),
        }}>
          <div style={{fontFamily: FONT_STACK, color: WHITE, fontSize: 44, lineHeight: 1.3, textAlign: "center"}}>
            Paga <span style={{color: RED, fontWeight: 800}}>R$ 5 mil</span> pra<br/>ganhar <span style={{color: RED, fontWeight: 800}}>R$ 4.800</span>
          </div>
        </div>
        <div style={{
          marginTop: 30,
          fontFamily: FONT_STACK, color: RED, fontSize: 64, fontWeight: 900, textAlign: "center", letterSpacing: 3,
          opacity: interpolate(f, [300, 350], [0, 1], CLAMP),
        }}>
          🩸 SANGRANDO
        </div>
      </div>
    </AbsoluteFill>
  );
};

// ─── S5 MISSÃO DO DIA ─────────────────────────────────
const S5 = () => {
  const f = useCurrentFrame();
  return (
    <AbsoluteFill style={{background: `linear-gradient(180deg, ${BLUE}33 0%, #0a0a0a 70%)`}}>
      <Audio src={staticFile("audio/cafe-ep20/c05.mp3")} volume={TTS_VOLUME} />
      <div style={{position: "absolute", top: 240, left: 80, right: 80}}>
        <div style={{fontFamily: FONT_STACK, color: YELLOW, fontSize: 48, letterSpacing: 3, marginBottom: 20, fontWeight: 800}}>
          ⚡ MISSÃO DO DIA
        </div>
        <AccentLine color={YELLOW} delay={6} />
        <div style={{
          fontFamily: FONT_STACK, color: WHITE, fontSize: 60, fontWeight: 700, marginTop: 60, lineHeight: 1.3,
          opacity: interpolate(f, [20, 60], [0, 1], CLAMP),
        }}>
          Calcule seu <span style={{color: YELLOW}}>C, A, C blended</span><br/>
          + seu <span style={{color: GREEN}}>L, T, V em 24 meses</span>
        </div>
        <div style={{
          marginTop: 50, padding: 40, borderRadius: 20, background: `${YELLOW}1A`, border: `2px solid ${YELLOW}`,
          opacity: interpolate(f, [80, 130], [0, 1], CLAMP),
        }}>
          <div style={{fontFamily: FONT_STACK, color: WHITE, fontSize: 56, fontWeight: 800, textAlign: "center"}}>
            Razão mínima:<br/>
            <span style={{color: YELLOW, fontSize: 96}}>3 × 1</span>
          </div>
        </div>
        <div style={{
          marginTop: 60,
          fontFamily: FONT_STACK, color: WHITE, fontSize: 42, lineHeight: 1.35,
          opacity: interpolate(f, [170, 220], [0, 1], CLAMP),
        }}>
          Comenta a <strong style={{color: GREEN}}>sua razão atual</strong><br/>aqui em baixo 👇
        </div>
      </div>
      <div style={{position: "absolute", bottom: 140, left: 0, right: 0, textAlign: "center"}}>
        <div style={{fontFamily: FONT_STACK, color: MUTED, fontSize: 30, letterSpacing: 4}}>@GUILHERMERIBEIRO.AI</div>
      </div>
    </AbsoluteFill>
  );
};

// ─── MAIN ─────────────────────────────────────────────────────────────
export const CafeEp20: React.FC = () => {
  const timeline: Array<{from: number; dur: number}> = [];
  let offset = 0;
  for (let i = 0; i < SCENES.length; i++) {
    timeline.push({from: offset, dur: SCENES[i].dur});
    offset += SCENES[i].dur;
    if (i < SCENES.length - 1) offset += TRANSITION_DUR;
  }

  return (
    <AbsoluteFill style={{backgroundColor: "#000"}}>
      <div style={{position: "absolute", inset: 0, background: `radial-gradient(ellipse at 50% 30%, ${BLUE}14, #000 70%)`}} />
      <Noise opacity={0.02} />
      <FilmGrain opacity={0.025} />
      <Vignette intensity={0.5} />

      <Audio
        src={staticFile("music/cafe-lofi.mp3")}
        volume={(frame) => {
          const total = CAFE_EP20_TOTAL;
          const fadeIn = interpolate(frame, [0, 45], [0, BGM_VOLUME], {extrapolateRight: "clamp"});
          const fadeOut = interpolate(frame, [total - 60, total], [BGM_VOLUME, 0], {extrapolateLeft: "clamp", extrapolateRight: "clamp"});
          return Math.min(fadeIn, fadeOut);
        }}
        loop
      />

      {SCENES.map(({dur}, i) => (
        <Sequence key={i} from={timeline[i].from} durationInFrames={dur}>
          <SceneFade duration={dur}>
            {[S1, S2, S3, S4, S5][i]()}
          </SceneFade>
        </Sequence>
      ))}
    </AbsoluteFill>
  );
};

CafeEp20.displayName = "CafeEp20";

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
